import isEmpty from "lodash/isEmpty";
import type { ApiResponse } from "../../../../types/api";
import { notify } from "../../../bugsnag";

const isJson = (text: string) => {
  try {
    JSON.parse(text);
    return true;
  } catch (e) {
    return false;
  }
};

export class UnsuccessfulError extends Error {
  response: Response;

  constructor(response: Response, message = "Unsuccessful Response") {
    super(message);
    this.name = "UnsuccessfulError";
    this.response = response;
  }
}

export class HttpError extends Error {
  response: Response;

  constructor(response: Response, message: string) {
    super(message);
    this.name = "HttpError";
    this.response = response;
  }
}

export const FetchUtils = {
  getRequestAuthorization: (init?: RequestInit) => {
    const headers = init ? Object.fromEntries(new Headers(init?.headers).entries()) : undefined;
    return headers?.Authorization;
  },
  guardedFetch: async (
    input: RequestInfo | URL,
    init?: RequestInit
  ) => {
    try {
      const response = await fetch(input, init);

      const plainTextBody = await response.clone().text();
      const isJsonResponse = !isEmpty(plainTextBody) && isJson(plainTextBody);
      const body = isJsonResponse ? JSON.parse(plainTextBody) as ApiResponse : null;

      if (response.status === 404) {
        throw new HttpError(
          response,
          "Record not found. Please confirm your data and try again."
        );
      } else if (response.status === 429 || response.status === 403) {
        throw new HttpError(
          response,
          "You are making too many requests, please wait five minutes and try again."
        );
      } else if (response.status === 401) {
        // input will always be the url, so it's safe to convert to string
        await notify({
          /* eslint-disable-next-line @typescript-eslint/no-base-to-string */
          errorMessage: `Unauthorized error on call: ${input.toString()}`,
          response,
          jwt: FetchUtils.getRequestAuthorization(init),
          isJsonResponse
        });
        throw new HttpError(response, "Something went wrong. Please confirm your data and try again.");
      } else if (response.status >= 400 && response.status < 500) {
        // input will always be the url, so it's safe to convert to string
        await notify({
          /* eslint-disable-next-line @typescript-eslint/no-base-to-string */
          errorMessage: `Unhandled client error on call: ${input.toString()}`,
          response,
          jwt: FetchUtils.getRequestAuthorization(init),
          isJsonResponse
        });
        throw new HttpError(response, "Something went wrong. Please confirm your data and try again.");
      } else if (response.status >= 500) {
        // input will always be the url, so it's safe to convert to string
        await notify({
          /* eslint-disable-next-line @typescript-eslint/no-base-to-string */
          errorMessage: `Server error on call: ${input.toString()}`,
          response,
          jwt: FetchUtils.getRequestAuthorization(init),
          isJsonResponse
        });
        throw new HttpError(response, "Request failed. Please try again.");
      } else if (body?.success === false) {
        throw new UnsuccessfulError(response, body?.error);
      } else {
        return response;
      }
    } catch (error) {
      // We already notified bugsnag before throw an HttpError so we don't need to notify again
      // We don't want to notify bugsnag if the error is an UnsuccessfulError
      if (!(error instanceof HttpError) && !(error instanceof UnsuccessfulError)) {
        await notify({
          /* eslint-disable-next-line @typescript-eslint/no-base-to-string */
          errorMessage: `Server error on call: ${input.toString()}`,
          error,
          jwt: FetchUtils.getRequestAuthorization(init),
          isJsonResponse: false
        });
      }

      throw error;
    }
  }
};

export const guardedFetch = FetchUtils.guardedFetch;
